import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { getFormattedDate, getListingExpirationDate } from 'dateUtils';
import DurationDaysField from 'components/DurationDaysField';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DealRoom } from 'models/DealRoom';

import './ProlongCampaignModal.styles.scss';

type ProlongCampaignModalProps = {
  className?: string;
  listingId: string;
  hasExpired: boolean;
  expirationDate: Date;
  updateDeal: (updatedDeal: DealRoom) => void;
} & ModalProps;

const ProlongCampaignModal: React.ForwardRefRenderFunction<
  ModalRef,
  ProlongCampaignModalProps
> = (props, ref) => {
  const {
    className,
    listingId,
    hasExpired,
    expirationDate,
    updateDeal,
    ...rest
  } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('prolong-campaign-modal', className);

  const { t } = useTranslation();

  const formattedExpirationDate = getFormattedDate(new Date(expirationDate));

  const onSubmit = useCallback(
    async ({ extensionDays }: { extensionDays: string }) => {
      const count = +extensionDays;

      try {
        const { data } = await api.listings.extendCampaign(listingId, count);

        const newExpirationDate = getListingExpirationDate(
          data?.listing?.expirationDate,
          data?.listing?.seekingExpressionOfInterestDate,
        );

        const formattedNewExpirationDate = getFormattedDate(
          new Date(newExpirationDate),
        );

        popSuccess(
          t('listingCampaignExtendedSuccessMsg', {
            count,
            expirationDate: formattedExpirationDate,
            newExpirationDate: formattedNewExpirationDate,
          }),
        );
        updateDeal(data);
        modal.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      listingId,
      popSuccess,
      t,
      formattedExpirationDate,
      updateDeal,
      modal,
      popServerError,
    ],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('extendListingCampaign')}
      {...rest}
    >
      <Form
        onSubmit={onSubmit}
        render={({ invalid, submitting, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <p>{`${t(
              hasExpired
                ? 'listingCampaignExpiredAt'
                : 'listingCampaignExpiresAt',
              {
                date: formattedExpirationDate,
              },
            )}\n${t('extendCampaignDescription')}`}</p>

            <DurationDaysField
              name="extensionDays"
              label={t('extendingFor')}
              placeholder="10"
              required
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button type="submit" disabled={invalid || submitting}>
                {t('extendCampaign')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(ProlongCampaignModal);
